import React from 'react';
import {
  Form, Table, Badge, Button,
} from 'react-bootstrap';

// Modes mapped to the on-wire profile.zone_overrides[i].mode value.
// 'not_set' is a UI-only sentinel — those rows are NOT sent in the payload,
// matching the strict backend semantics where missing == unconfigured.
const MODES = [
  { value: 'not_set', label: 'Not set' },
  { value: 'override', label: 'Override' },
  { value: 'opt_out', label: 'Opt out' },
];

function OperatorZoneOverridesTable({
  serviceAreas = [],
  zoneOverrides = [],
  currency,
  onChange,
}) {
  const overridesById = new Map(
    zoneOverrides.map((zo) => [zo.service_area_id, zo]),
  );

  const setMode = (serviceAreaId, mode) => {
    const next = zoneOverrides.filter((zo) => zo.service_area_id !== serviceAreaId);
    if (mode === 'opt_out') {
      next.push({ service_area_id: serviceAreaId, mode: 'opt_out' });
    } else if (mode === 'override') {
      next.push({
        service_area_id: serviceAreaId,
        mode: 'override',
        price_multiplier: 1.0,
      });
    }
    // 'not_set' = remove from list
    onChange(next);
  };

  const updateField = (serviceAreaId, field, value) => {
    const next = zoneOverrides.map((zo) => {
      if (zo.service_area_id !== serviceAreaId) return zo;
      return { ...zo, [field]: value };
    });
    onChange(next);
  };

  const optOutAll = () => {
    onChange(
      serviceAreas.map((sa) => ({ service_area_id: sa.Id, mode: 'opt_out' })),
    );
  };

  const clearAll = () => {
    onChange([]);
  };

  const activeAreas = serviceAreas.filter((sa) => sa.active === 1);
  const notSetCount = activeAreas.filter(
    (sa) => !overridesById.has(sa.Id),
  ).length;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h5 className="mb-0">Zone Overrides</h5>
          {notSetCount > 0 ? (
            <Badge bg="warning" text="dark">
              {`${notSetCount} not set — quotes will fail for trips that match these zones`}
            </Badge>
          ) : (
            <Badge bg="success">All zones configured</Badge>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-warning" size="sm" onClick={optOutAll}>
            Opt out of all zones
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={clearAll}>
            Reset all to Not set
          </Button>
        </div>
      </div>

      <Table responsive bordered size="sm">
        <thead className="table-light">
          <tr>
            <th>Zone</th>
            <th style={{ width: '160px' }}>Mode</th>
            <th>Multiplier</th>
            <th>{`Flat rate${currency ? ` (${currency})` : ''}`}</th>
            <th>Applies to</th>
          </tr>
        </thead>
        <tbody>
          {activeAreas.map((sa) => {
            const override = overridesById.get(sa.Id);
            const mode = override?.mode ?? 'not_set';
            return (
              <tr key={sa.Id}>
                <td>
                  <strong>{sa.name}</strong>
                  {sa.is_base_location && (
                    <Badge bg="primary" className="ms-2">Base</Badge>
                  )}
                </td>
                <td>
                  <Form.Select
                    size="sm"
                    aria-label={`Mode for ${sa.name}`}
                    value={mode}
                    onChange={(e) => setMode(sa.Id, e.target.value)}
                  >
                    {MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Form.Select>
                </td>
                <td>
                  {mode === 'override' ? (
                    <Form.Control
                      type="number"
                      size="sm"
                      step="0.01"
                      min={0}
                      value={override?.price_multiplier ?? 1}
                      onChange={(e) => updateField(sa.Id, 'price_multiplier', Number(e.target.value))}
                    />
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td>
                  {mode === 'override' ? (
                    <Form.Control
                      type="number"
                      size="sm"
                      step="0.01"
                      min={0}
                      value={override?.flat_rate ?? ''}
                      onChange={(e) => updateField(sa.Id, 'flat_rate', e.target.value === '' ? undefined : Number(e.target.value))}
                      placeholder="—"
                    />
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td>
                  {mode === 'override' ? (
                    <Form.Select
                      size="sm"
                      value={override?.flat_rate_applies_to ?? 'origin'}
                      onChange={(e) => updateField(sa.Id, 'flat_rate_applies_to', e.target.value)}
                    >
                      <option value="origin">Origin</option>
                      <option value="destination">Destination</option>
                      <option value="both">Both</option>
                    </Form.Select>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
          {activeAreas.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center text-muted">
                No active service areas
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

export default OperatorZoneOverridesTable;
